import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MapPin, Search, Loader2, AlertTriangle, Radio, Zap, ShieldCheck, Sparkles,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { searchPlaces } from '@/lib/nominatim';
import { SUGGESTED_CITIES } from '@/data/suggested-cities';
import { useGameStore } from '@/state/gameStore';
import type { GameMode, HomeCity } from '@/state/types';

const DEBOUNCE_MS = 400;

type Step = 'mode' | 'city' | 'confirm';

interface ModeOption {
  id: GameMode;
  label: string;
  tagline: string;
  description: string;
  icon: typeof Sparkles;
  ringClass: string;
  badgeClass: string;
}

const MODE_OPTIONS: ModeOption[] = [
  {
    id: 'normal',
    label: 'Normal',
    tagline: 'Ausbalancierter Spielmodus',
    description:
      'Standard-Schwierigkeit. Faire Spawn-Raten, alle Mechaniken aktiv.',
    icon: Sparkles,
    ringClass: 'border-accent-dispatch',
    badgeClass: 'bg-accent-dispatch text-white',
  },
  {
    id: 'realistic',
    label: 'Realistisch',
    tagline: 'Mehr Tempo, Pause kostet Punkte',
    description:
      'Höhere Spawn-Frequenz, weniger Startkapital, Pause reduziert Punkte.',
    icon: Zap,
    ringClass: 'border-amber-400',
    badgeClass: 'bg-amber-500 text-white',
  },
  {
    id: 'sandbox',
    label: 'Sandbox',
    tagline: 'Frei bauen, kein Druck',
    description:
      'Unbegrenzte Credits, keine Bauzeiten, frei konfigurierbares Spawning.',
    icon: ShieldCheck,
    ringClass: 'border-status-available',
    badgeClass: 'bg-status-available text-bg-primary',
  },
];

export function CityPicker() {
  const setHomeCity = useGameStore((s) => s.setHomeCity);
  const setMode = useGameStore((s) => s.setMode);
  const currentMode = useGameStore((s) => s.mode);

  // Phase 7.1.1 — pick up the SaveBrowser's "create-new-slot" mode hint, set
  // it as the new game's mode, then clear it.
  const [hasPresetMode, setHasPresetMode] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const pending = sessionStorage.getItem('rescueop_pending_mode');
      if (pending === 'normal' || pending === 'realistic' || pending === 'sandbox') {
        setMode(pending);
        sessionStorage.removeItem('rescueop_pending_mode');
        setHasPresetMode(true);
      }
    } catch {
      // ignore
    }
  }, [setMode]);

  // Phase 8.6.1 — Wizard steps. If a mode was preset (from SaveBrowser),
  // jump straight to city selection; otherwise start at mode selection.
  const [step, setStep] = useState<Step>(hasPresetMode ? 'city' : 'mode');
  useEffect(() => {
    if (hasPresetMode) setStep('city');
  }, [hasPresetMode]);

  const [pendingCity, setPendingCity] = useState<HomeCity | null>(null);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<HomeCity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<number | null>(null);

  const runSearch = useCallback(async (q: string) => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const list = await searchPlaces(q, ctrl.signal);
      if (!ctrl.signal.aborted) setResults(dedupeCities(list));
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      setError('Suche nicht verfügbar. Bitte Verbindung prüfen oder einen Vorschlag wählen.');
      setResults([]);
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => runSearch(query), DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query, runSearch]);

  const chooseCity = (city: HomeCity) => {
    setPendingCity(city);
    setStep('confirm');
  };

  const confirmStart = (adjusted: HomeCity) => {
    setHomeCity(adjusted);
  };

  const stepIndex =
    step === 'mode' ? 0 : step === 'city' ? 1 : 2;
  const totalSteps = hasPresetMode ? 2 : 3;

  return (
    <main className="flex min-h-screen flex-col bg-bg-primary text-fg-primary">
      <header className="flex h-14 flex-shrink-0 items-center border-b border-border-subtle bg-bg-secondary px-4">
        <Link to="/" className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-emergency">
            <Radio className="h-3.5 w-3.5 text-white" aria-hidden />
          </span>
          <span className="font-display text-base font-semibold">
            Dispatch<span className="text-accent-emergency">24</span>
          </span>
        </Link>
      </header>

      <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col px-4 py-10 md:py-16">
        <p className="mb-2 text-sm uppercase tracking-widest text-accent-dispatch">
          Schritt {stepIndex + (hasPresetMode ? 0 : 1)} / {totalSteps}
        </p>

        {step === 'mode' && (
          <ModeStep
            currentMode={currentMode}
            onChoose={(m) => {
              setMode(m);
              setStep('city');
            }}
          />
        )}

        {step === 'city' && (
          <CityStep
            query={query}
            setQuery={setQuery}
            loading={loading}
            error={error}
            results={results}
            onChoose={chooseCity}
            onBack={hasPresetMode ? null : () => setStep('mode')}
          />
        )}

        {step === 'confirm' && pendingCity && (
          <ConfirmStep
            city={pendingCity}
            mode={currentMode}
            onBack={() => setStep('city')}
            onConfirm={confirmStart}
          />
        )}

        <p className="mt-auto pt-10 text-xs text-fg-muted">
          Kartendaten © OpenStreetMap-Mitwirkende. Geocoding via Nominatim.
        </p>
      </div>
    </main>
  );
}

/* ──────────────── Step 1: Mode ──────────────── */

function ModeStep({
  currentMode,
  onChoose,
}: {
  currentMode: GameMode;
  onChoose: (m: GameMode) => void;
}) {
  return (
    <>
      <h1 className="font-display text-3xl font-bold md:text-5xl">Wähle deinen Spielmodus.</h1>
      <p className="mt-3 max-w-xl text-fg-secondary">
        Der Modus bestimmt Tempo und Schwierigkeit. Du kannst ihn später nicht ändern, ohne ein
        neues Spiel zu starten.
      </p>

      <div className="mt-8 grid gap-3 sm:grid-cols-1">
        {MODE_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const isCurrent = currentMode === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onChoose(opt.id)}
              className={[
                'group flex items-start gap-4 rounded-lg border-2 bg-bg-secondary p-4 text-left transition',
                isCurrent
                  ? `${opt.ringClass} bg-bg-elevated`
                  : 'border-border-subtle hover:border-border-strong hover:bg-bg-elevated',
              ].join(' ')}
            >
              <span
                className={[
                  'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg',
                  opt.badgeClass,
                ].join(' ')}
              >
                <Icon className="h-6 w-6" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-display text-lg font-bold">{opt.label}</p>
                <p className="text-[11px] uppercase tracking-wider text-fg-muted">
                  {opt.tagline}
                </p>
                <p className="mt-1 text-xs text-fg-secondary">{opt.description}</p>
              </div>
              <ChevronRight className="h-4 w-4 flex-shrink-0 text-fg-muted transition group-hover:translate-x-0.5 group-hover:text-fg-primary" aria-hidden />
            </button>
          );
        })}
      </div>
    </>
  );
}

/* ──────────────── Step 2: City ──────────────── */

function CityStep({
  query,
  setQuery,
  loading,
  error,
  results,
  onChoose,
  onBack,
}: {
  query: string;
  setQuery: (s: string) => void;
  loading: boolean;
  error: string | null;
  results: HomeCity[];
  onChoose: (c: HomeCity) => void;
  onBack: (() => void) | null;
}) {
  return (
    <>
      <h1 className="font-display text-3xl font-bold md:text-5xl">Wähle deine Stadt.</h1>
      <p className="mt-3 max-w-xl text-fg-secondary">
        Jede Stadt, jedes Dorf in Deutschland, Österreich oder der Schweiz. Dein Einsatzgebiet
        startet mit 5 km Radius und wächst mit jedem Level.
      </p>

      <div className="relative mt-10">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted"
          aria-hidden
        />
        <input
          type="search"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="z. B. Berlin, Kemnath, Garmisch-Partenkirchen, Zürich…"
          className="w-full rounded-md border border-border-strong bg-bg-secondary py-3 pl-10 pr-10 text-base text-fg-primary placeholder:text-fg-muted focus:border-accent-dispatch focus:outline-none"
        />
        {loading && (
          <Loader2
            className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-fg-muted"
            aria-hidden
          />
        )}
      </div>

      {error && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-status-enroute/50 bg-status-enroute/10 p-3 text-sm text-status-enroute">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
          <p>{error}</p>
        </div>
      )}

      {results.length > 0 && (
        <ul className="mt-3 overflow-hidden rounded-md border border-border-subtle bg-bg-secondary">
          {results.map((r, i) => (
            <li key={`${r.displayName}-${r.center.lat}-${r.center.lng}-${i}`}>
              <button
                type="button"
                onClick={() => onChoose(r)}
                className="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-bg-elevated"
              >
                <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent-dispatch" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold">{r.displayName}</p>
                  <p className="truncate text-xs text-fg-muted">
                    {[r.state, r.country].filter(Boolean).join(' · ')}
                  </p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-10">
        <p className="mb-3 text-xs uppercase tracking-widest text-fg-muted">
          Beliebte Städte zum schnellen Einstieg
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {SUGGESTED_CITIES.map((c) => (
            <button
              key={c.displayName}
              type="button"
              onClick={() => onChoose(c)}
              className="group flex items-start gap-3 rounded-md border border-border-subtle bg-bg-secondary p-3 text-left transition hover:border-border-strong hover:bg-bg-elevated"
            >
              <MapPin
                className="mt-0.5 h-4 w-4 flex-shrink-0 text-fg-muted transition-colors group-hover:text-accent-dispatch"
                aria-hidden
              />
              <div>
                <p className="font-semibold">{c.displayName}</p>
                <p className="text-xs text-fg-muted">
                  {[c.state, c.country].filter(Boolean).join(' · ')}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="mt-6 inline-flex items-center gap-1.5 self-start text-xs font-semibold text-fg-muted transition hover:text-fg-primary"
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
          Zurück zur Modus-Auswahl
        </button>
      )}
    </>
  );
}

/* ──────────────── Step 3: Confirm + verschiebbarer Kreis ──────────────── */

function ConfirmStep({
  city,
  mode,
  onBack,
  onConfirm,
}: {
  city: HomeCity;
  mode: GameMode;
  onBack: () => void;
  onConfirm: (adjusted: HomeCity) => void;
}) {
  // Phase 8.6.1 — Verschiebbarer Kreis. The user can nudge the start center
  // ±0.05 ° in lat/lng using the arrow keys or sliders. The result is committed
  // to the chosen city before setHomeCity is called.
  const [offsetLat, setOffsetLat] = useState(0);
  const [offsetLng, setOffsetLng] = useState(0);

  const adjustedCity: HomeCity = {
    ...city,
    center: {
      lat: city.center.lat + offsetLat,
      lng: city.center.lng + offsetLng,
    },
  };

  const modeLabel = MODE_OPTIONS.find((m) => m.id === mode)?.label ?? mode;

  return (
    <>
      <h1 className="font-display text-3xl font-bold md:text-4xl">Bestätigen &amp; loslegen.</h1>
      <p className="mt-3 max-w-xl text-fg-secondary">
        Du kannst den Mittelpunkt deines Einsatzgebiets noch leicht verschieben.
      </p>

      <div className="mt-8 rounded-lg border border-border-subtle bg-bg-secondary p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-accent-dispatch/15 text-accent-dispatch">
            <MapPin className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-display text-lg font-bold">{city.displayName}</p>
            <p className="text-xs text-fg-muted">
              {[city.state, city.country].filter(Boolean).join(' · ')}
            </p>
          </div>
          <span className="rounded-full bg-bg-elevated px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-fg-secondary">
            {modeLabel}
          </span>
        </div>

        {/* Schematic preview */}
        <div className="mt-5 rounded-lg border border-border-subtle bg-bg-primary p-4">
          <div className="relative mx-auto h-40 w-full overflow-hidden rounded-md bg-bg-elevated">
            {/* Grid */}
            <div
              aria-hidden
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage:
                  'linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)',
                backgroundSize: '20px 20px',
              }}
            />
            {/* Radius circle, repositioned by the offset */}
            <div
              className="absolute h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-accent-dispatch bg-accent-dispatch/15"
              style={{
                left: `calc(50% + ${offsetLng * 1500}px)`,
                top: `calc(50% - ${offsetLat * 1500}px)`,
              }}
            />
            {/* City pin (fixed) */}
            <div className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-fg-primary shadow-[0_0_0_3px_rgba(0,0,0,0.4)]" />
          </div>

          <div className="mt-4 space-y-3">
            <SliderRow
              label="Verschiebung Nord/Süd"
              value={offsetLat}
              min={-0.05}
              max={0.05}
              step={0.005}
              onChange={setOffsetLat}
              displayUnit="°"
            />
            <SliderRow
              label="Verschiebung Ost/West"
              value={offsetLng}
              min={-0.05}
              max={0.05}
              step={0.005}
              onChange={setOffsetLng}
              displayUnit="°"
            />
          </div>

          <div className="mt-3 flex items-center justify-between text-[11px] text-fg-muted">
            <span>
              Mittelpunkt:{' '}
              <span className="font-semibold tabular-nums text-fg-primary">
                {adjustedCity.center.lat.toFixed(4)}, {adjustedCity.center.lng.toFixed(4)}
              </span>
            </span>
            <button
              type="button"
              onClick={() => {
                setOffsetLat(0);
                setOffsetLng(0);
              }}
              className="rounded-md bg-bg-elevated px-2 py-1 text-[10px] font-semibold transition hover:bg-bg-elevated/70"
            >
              Zurücksetzen
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-fg-muted transition hover:text-fg-primary"
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
          Andere Stadt
        </button>
        <button
          type="button"
          onClick={() => onConfirm(adjustedCity)}
          className="inline-flex items-center gap-1.5 rounded-md bg-accent-emergency px-5 py-2.5 text-sm font-bold text-white shadow-lg transition hover:bg-accent-emergency-hover"
        >
          <ShieldCheck className="h-4 w-4" aria-hidden />
          Spiel starten
        </button>
      </div>
    </>
  );
}

function SliderRow({
  label,
  value,
  min,
  max,
  step,
  onChange,
  displayUnit,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  displayUnit?: string;
}) {
  return (
    <label className="block">
      <div className="mb-1 flex items-center justify-between text-[11px]">
        <span className="text-fg-muted">{label}</span>
        <span className="font-semibold tabular-nums text-fg-primary">
          {value > 0 ? '+' : ''}
          {value.toFixed(3)}
          {displayUnit ?? ''}
        </span>
      </div>
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-accent-dispatch"
      />
    </label>
  );
}

/**
 * Phase 11.3.4 (Bug 15) — Nominatim returns duplicate suggestions for the same
 * city when both a "Stadt" and a "Gemeinde" entry exist (Garmisch-Partenkirchen
 * was the canonical example). De-duplicate by rounded coordinates + display
 * name; keep the first occurrence which is the highest-importance match.
 */
function dedupeCities(list: HomeCity[]): HomeCity[] {
  const seen = new Set<string>();
  const out: HomeCity[] = [];
  for (const item of list) {
    const key =
      `${item.center.lat.toFixed(3)}_${item.center.lng.toFixed(3)}_${item.displayName.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}
